/**
 * LeakLedger.tsx — Section 4
 * The worst leak points as cards, each with four counts (Entered / In-flight /
 * Confirmed lost / Moved on), decoded percentages, a recoverable estimate,
 * plus curated why/action lines. Every count is null-safe: a missing live
 * source renders "Awaiting telemetry" (never a fabricated 0).
 *
 * The page's ONE time filter (in the header) drives both:
 *   - who entered each step (audit cohort date range)
 *   - when they count as confirmed-lost (staleness = length of that period)
 *
 * This section used to render a SECOND copy of that filter. It now receives the
 * selected range as a prop and only reports what the window resolved to.
 */

import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import SectionHeader from './SectionHeader';
import { FONT, FONT_H, FONT_MONO, LEAK_COUNT_COLORS, SEVERITY_CHIP, SURFACE, AWAITING_LABEL, fmtWindowHours } from './funnelBands';
import { TimeRangeValue, rangeDays } from '../common/timeRange';
import { FunnelRangeMeta, LeakLedgerItem } from './activationTypes';
import { LEAK_LEDGER_COPY } from './leakLedgerCopy';

interface Props {
  leaks: LeakLedgerItem[];
  loading: boolean;
  refreshing?: boolean;
  /** The page-level selection — read-only here; the control lives in the header. */
  range: TimeRangeValue;
  rangeMeta?: FunnelRangeMeta | null;
  /** Derived from the same date filter — used for copy only. */
  windowHours: number;
}

// fmtWindowHours is shared from funnelBands — the local copy disagreed with
// the page header at exactly 24h ("1d" vs "24h").

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return iso.slice(0, 10);
};

const LEGEND = [
  { color: LEAK_COUNT_COLORS.entered, label: 'Entered', desc: 'distinct Members who reached this step in the selected period' },
  { color: LEAK_COUNT_COLORS.flight, label: 'In-flight', desc: 'reached it within this same period and have not advanced yet' },
  { color: LEAK_COUNT_COLORS.lost, label: 'Confirmed lost', desc: 'reached it and sat the full selected period without advancing' },
  { color: LEAK_COUNT_COLORS.moved, label: 'Moved on', desc: 'reached this step and the next — healthy progression' },
];

const CountCell: React.FC<{ label: string; value: number | null; note: string; color: string; nullLabel?: string }> = ({ label, value, note, color, nullLabel }) => (
  <Box sx={{ border: `1px solid ${SURFACE.line}`, borderTop: `2.5px solid ${color}`, borderRadius: '9px', p: '9px 9px 8px', bgcolor: '#fff' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
      <Typography sx={{ fontFamily: FONT_MONO, fontSize: '8.5px', letterSpacing: '0.03em', textTransform: 'uppercase', color: SURFACE.slate, lineHeight: 1.2 }}>
        {label}
      </Typography>
    </Box>
    {value === null ? (
      <Typography sx={{ fontFamily: FONT, fontSize: '10px', fontStyle: 'italic', color: '#9CA3AF', mt: 0.6, lineHeight: 1.2 }}>
        {nullLabel ?? AWAITING_LABEL}
      </Typography>
    ) : (
      <>
        <Typography sx={{ fontFamily: FONT_MONO, fontSize: '22px', fontWeight: 800, mt: 0.5, lineHeight: 1, color }}>
          {value.toLocaleString()}
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: '9px', color: SURFACE.slate, mt: 0.25, lineHeight: 1.2 }}>
          {note}
        </Typography>
      </>
    )}
  </Box>
);

const DecodeRow: React.FC<{ k: string; children: React.ReactNode; noBorder?: boolean }> = ({ k, children, noBorder }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '116px 1fr', columnGap: 1.25, alignItems: 'baseline', py: 0.6, borderBottom: noBorder ? 'none' : '1px solid #f0e8d6' }}>
    <Typography sx={{ fontFamily: FONT_MONO, fontSize: '10px', letterSpacing: '0.02em', textTransform: 'uppercase', color: SURFACE.slate }}>
      {k}
    </Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: '#333', textAlign: 'left' }}>
      {children}
    </Typography>
  </Box>
);

/**
 * MMP-CAP. The staleness window is NOT always the date filter.
 *
 * `MmpAnalyticsPage.resolveWindowHours` clamps to the API's 720h maximum, so a
 * 90D cohort — and any custom span longer than 30 days — is aged on 30 days
 * while the filter says 90. This row printed "(same as the date filter)"
 * unconditionally, two inches under a caption that said the opposite for 90D.
 */
const LeakCard: React.FC<{ leak: LeakLedgerItem; rank: number; clamped: boolean }> = ({ leak, rank, clamped }) => {
  const sev = SEVERITY_CHIP[leak.severity] || SEVERITY_CHIP.Minor;
  const copy = LEAK_LEDGER_COPY[leak.i];
  const awaiting = leak.split_status !== 'live';

  return (
    <Box sx={{ bgcolor: SURFACE.paper, border: `1px solid ${SURFACE.line}`, borderTop: `4px solid ${sev.border}`, borderRadius: '14px', p: '16px 18px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.25 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT_H, fontSize: '17px', fontWeight: 700, lineHeight: 1.13, color: SURFACE.ink }}>
            {rank}. {leak.name}
          </Typography>
          <Typography sx={{ fontFamily: FONT_MONO, fontSize: '9.5px', letterSpacing: '0.04em', textTransform: 'uppercase', color: SURFACE.slate, mt: 0.4 }}>
            Step {leak.i} · Phase {leak.phase_display_index ?? leak.phase} · {leak.event_name}
          </Typography>
        </Box>
        <Box sx={{ bgcolor: sev.bg, borderRadius: '999px', px: 1.1, py: 0.4, flexShrink: 0 }}>
          <Typography sx={{ fontFamily: FONT_MONO, fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: sev.fg, whiteSpace: 'nowrap' }}>
            {leak.severity}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.9, mt: 1.75, mb: 0.5 }}>
        <CountCell label="Entered" value={leak.entered} note="reached step" color={LEAK_COUNT_COLORS.entered} />
        {/* "No recency source" ≠ "Awaiting telemetry": the step's events exist,
            but this step type has no per-user timestamp to age the split by. */}
        <CountCell label="In-flight" value={awaiting ? null : leak.in_flight} note="still in period" color={LEAK_COUNT_COLORS.flight} nullLabel="No recency source" />
        <CountCell label="Confirmed lost" value={awaiting ? null : leak.confirmed_dropped} note="sat full period" color={LEAK_COUNT_COLORS.lost} nullLabel="No recency source" />
        <CountCell label="Moved on" value={leak.moved_on} note="advanced" color={LEAK_COUNT_COLORS.moved} />
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <DecodeRow k="% of step">
          {leak.advanced_pct !== null ? <><b style={{ fontFamily: FONT_MONO }}>{leak.advanced_pct.toFixed(0)}%</b> of those who reached it advanced</> : '—'}
        </DecodeRow>
        <DecodeRow k="% of total">
          {leak.pct_of_installs !== null ? <><b style={{ fontFamily: FONT_MONO }}>{leak.pct_of_installs.toFixed(1)}%</b> of new Members reach here</> : '—'}
        </DecodeRow>
        <DecodeRow k="Lost after">
          {/* Printed from the API's own staleness_window_hours — the number
              the split was actually computed with — never the client's copy
              of the filter, which could silently disagree after clamping. */}
          {leak.staleness_window_hours !== null
            ? <><b style={{ fontFamily: FONT_MONO }}>{fmtWindowHours(leak.staleness_window_hours)}</b> without advancing {clamped ? '(the date filter, capped at 30 days)' : '(same as the date filter)'}</>
            : <span style={{ fontStyle: 'italic', color: '#9CA3AF' }}>No recency source</span>}
        </DecodeRow>
        <DecodeRow k="Recoverable" noBorder>
          {leak.recoverable_status === 'live' && leak.recoverable !== null
            ? <b style={{ fontFamily: FONT_MONO, color: '#146152' }}>~{Math.round(leak.recoverable).toLocaleString()}/mo</b>
            : <span style={{ fontStyle: 'italic', color: '#9CA3AF' }}>{AWAITING_LABEL}</span>}
        </DecodeRow>
      </Box>

      {copy && (
        <>
          <Box sx={{ mt: 1.5, bgcolor: SURFACE.cream, borderRadius: '8px', p: '9px 12px' }}>
            <Typography sx={{ fontFamily: FONT, fontSize: '12.5px', color: '#444', lineHeight: 1.5 }}>
              <b style={{ color: '#7D1935' }}>Why a leak:</b> {copy.why}
            </Typography>
          </Box>
          <Box sx={{ mt: 1, bgcolor: '#e6f0ec', border: '1px solid #cfe1d8', borderRadius: '8px', p: '9px 12px' }}>
            <Typography sx={{ fontFamily: FONT, fontSize: '12.5px', color: '#27483f', lineHeight: 1.5 }}>
              <b style={{ color: '#146152' }}>Action:</b> {copy.action}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

const LeakLedger: React.FC<Props> = ({
  leaks,
  loading,
  refreshing,
  range,
  rangeMeta,
  windowHours,
}) => {
  // 720h is the API's maximum staleness window. Any filter longer than 30 days
  // — the 90D preset, or a custom span over 30 days — is aged on 30 days while
  // the pill keeps saying 90. One flag, so the row copy and the caption below
  // cannot disagree with each other again.
  const stalenessClamped = windowHours === 720 && rangeDays(range) > 30;
  const customSpanClamped = range.type === 'custom' && stalenessClamped;

  return (
  <Box id="leak-ledger" sx={{ scrollMarginTop: '16px' }}>
    <SectionHeader
      num={4}
      title="Leak Ledger"
      description="Shows the worst leak points step-by-step — who entered, who's still in-flight, who's lost, who moved on — plus the recommended fix for each. The page's time filter controls the audit period and when someone counts as lost."
    />

    {/* Resolved window — the control itself lives once, in the page header. */}
    <Box sx={{ bgcolor: SURFACE.paper, border: `1px solid ${SURFACE.line}`, borderRadius: '10px', p: '12px 15px', mb: 1.25 }}>
      {refreshing && (
        <Typography sx={{ fontFamily: FONT, fontSize: '10px', color: SURFACE.slate, fontStyle: 'italic' }}>updating…</Typography>
      )}

      {rangeMeta && (
        <Box sx={{ mt: 1.25 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: SURFACE.ink, lineHeight: 1.45 }}>
            Showing{' '}
            <b style={{ fontFamily: FONT_MONO }}>{fmtDate(rangeMeta.effective_from)}</b>
            {' → '}
            <b style={{ fontFamily: FONT_MONO }}>{fmtDate(rangeMeta.effective_to)}</b>
            {' · '}lost after{' '}
            <b style={{ fontFamily: FONT_MONO }}>{fmtWindowHours(windowHours)}</b>
            {' '}without advancing
          </Typography>
          {rangeMeta.epoch_clamped && (
            <Box sx={{ mt: 0.75, bgcolor: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '8px', p: '8px 12px' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: '#9A3412', lineHeight: 1.45 }}>
                Requested start <b style={{ fontFamily: FONT_MONO }}>{fmtDate(rangeMeta.requested_from)}</b> is
                before the funnel epoch <b style={{ fontFamily: FONT_MONO }}>{fmtDate(rangeMeta.funnel_epoch)}</b>.
                Counts start at the epoch — earlier data is excluded by design.
              </Typography>
            </Box>
          )}
          {range.type === '90d' && windowHours === 720 && (
            <Typography sx={{ fontFamily: FONT, fontSize: '11px', color: SURFACE.slate, mt: 0.75, lineHeight: 1.4 }}>
              90D cohort uses a 30d lost threshold (API maximum).
            </Typography>
          )}
          {/* The same clamp, said out loud for the case that used to say
              nothing at all: a custom span longer than 30 days. */}
          {customSpanClamped && (
            <Typography sx={{ fontFamily: FONT, fontSize: '11px', color: SURFACE.slate, mt: 0.75, lineHeight: 1.4 }}>
              This {rangeDays(range)}-day cohort uses a 30d lost threshold (API maximum).
            </Typography>
          )}
        </Box>
      )}
    </Box>

    <Box sx={{ display: 'flex', gap: 2.25, flexWrap: 'wrap', bgcolor: SURFACE.paper, border: `1px solid ${SURFACE.line}`, borderRadius: '10px', p: '11px 15px', mb: 1.75 }}>
      {LEGEND.map(l => (
        <Box key={l.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.9, flex: '1 1 220px' }}>
          <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: l.color, mt: '4px', flexShrink: 0 }} />
          <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: SURFACE.slate, lineHeight: 1.4 }}>
            <b style={{ color: SURFACE.ink }}>{l.label}</b> · {l.desc}
          </Typography>
        </Box>
      ))}
    </Box>

    {loading ? (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.75 }}>
        {Array(4).fill(null).map((_, i) => <Skeleton key={i} variant="rounded" height={280} sx={{ borderRadius: '14px' }} />)}
      </Box>
    ) : leaks.length === 0 ? (
      <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: SURFACE.slate, fontStyle: 'italic', p: 2 }}>
        No leak data yet — awaiting telemetry from instrumented steps.
      </Typography>
    ) : (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.75 }}>
        {leaks.map((leak, idx) => (
          <LeakCard key={leak.i} leak={leak} rank={idx + 1} clamped={stalenessClamped} />
        ))}
      </Box>
    )}

    <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: SURFACE.slate, fontStyle: 'italic', mt: 1.5 }}>
      Sorted by the largest step-to-step drop. In-flight vs confirmed-lost uses{' '}
      {stalenessClamped
        ? 'the audit period capped at the API maximum of 30 days.'
        : 'the same time filter as the audit period.'}
    </Typography>
  </Box>
  );
};

export default LeakLedger;
